import { useState } from "react";
import { generateWeights, DEFAULT_SCORE_WEIGHTS } from "@/utils/weights";

export default function WeightSettings({ vacancy, onUpdate }) {
  const [scoreWeights, setScoreWeights] = useState(DEFAULT_SCORE_WEIGHTS);

  const totalScoreWeight = Object.values(scoreWeights).reduce(
    (sum, weight) => sum + weight,
    0,
  );

  const updateScoreWeights = () => {
    const weights = generateWeights(vacancy);
    setScoreWeights(weights);
    onUpdate(weights);
  };

  const reset = () => {
    setScoreWeights(DEFAULT_SCORE_WEIGHTS);
    onUpdate(DEFAULT_SCORE_WEIGHTS);
  };

  const handleWeightChange = (dimension, value) => {
    const newValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    const otherDimensions = Object.keys(scoreWeights).filter(
      (key) => key !== dimension,
    );
    const newWeights = { ...scoreWeights, [dimension]: newValue };

    // 计算剩余权重
    let remaining = 100 - newValue;
    let temp = {};
    let sum = 0;
    const otherTotal =
      otherDimensions.reduce((s, k) => s + scoreWeights[k], 0) || 1;

    otherDimensions.forEach((key, idx) => {
      if (idx === otherDimensions.length - 1) {
        temp[key] = remaining - sum;
      } else {
        temp[key] = Math.floor((remaining * scoreWeights[key]) / otherTotal);
        sum += temp[key];
      }
    });

    otherDimensions.forEach((key) => {
      newWeights[key] = Math.max(0, Math.min(100, temp[key]));
    });

    // 再次校正，确保总和为100
    let total = Object.values(newWeights).reduce((a, b) => a + b, 0);
    // 微调，优先调整非当前项
    const adjustKeys = otherDimensions.concat([dimension]);
    let i = 0;
    while (total !== 100 && i < 20) {
      // 最多调整20次，防止死循环
      for (let key of adjustKeys) {
        if (total === 100) break;
        if (total > 100 && newWeights[key] > 0) {
          newWeights[key] -= 1;
          total -= 1;
        } else if (total < 100 && newWeights[key] < 100) {
          newWeights[key] += 1;
          total += 1;
        }
      }
      i++;
    }

    setScoreWeights(newWeights);
    onUpdate(weights);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">评分权重调整</h3>
          <p className="text-sm text-gray-500 mt-1">
            根据职位需求调整各维度的评分权重，总权重需保持100%
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full">
            <span className="text-sm text-blue-700">总权重</span>
            <span className="text-sm font-medium text-blue-900">
              {totalScoreWeight}%
            </span>
          </div>
          <button
            onClick={updateScoreWeights}
            className="inline-flex items-center px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            智能调整
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            重置默认
          </button>
        </div>
      </div>
      <div className="space-y-4">
        {Object.entries(scoreWeights).map(([dimension, weight]) => {
          const dimensionLabels = {
            technicalSkills: "技术技能",
            projectExperience: "项目经验",
            industryBackground: "行业背景",
            softSkills: "软技能",
            education: "教育背景",
            careerProgression: "职业发展",
          };
          return (
            <div
              key={dimension}
              className="flex items-center gap-3 py-2 border-b last:border-b-0 border-gray-100"
            >
              <span className="w-20 text-sm text-gray-700 font-medium">
                {dimensionLabels[dimension]}
              </span>
              <button
                onClick={() => handleWeightChange(dimension, weight - 5)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 transition"
                disabled={weight <= 0}
                tabIndex={-1}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M20 12H4"
                  />
                </svg>
              </button>
              <button
                onClick={() => handleWeightChange(dimension, weight + 5)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 transition"
                disabled={
                  Object.values(scoreWeights).reduce((sum, w) => sum + w, 0) >=
                  100
                }
                tabIndex={-1}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
              <input
                type="number"
                min="0"
                max="100"
                value={weight}
                onChange={(e) => handleWeightChange(dimension, e.target.value)}
                className="w-16 text-center border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mx-2"
                style={{ fontVariantNumeric: "tabular-nums" }}
              />
              <span className="text-gray-400 text-sm">%</span>
              <input
                type="range"
                min="0"
                max="100"
                value={weight}
                onChange={(e) => handleWeightChange(dimension, e.target.value)}
                className="flex-1 accent-blue-500 mx-4"
                style={{ minWidth: 80 }}
              />
              <span className="text-gray-500 font-medium w-10 text-right">
                {weight}%
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-500 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">权重调整说明</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600">
              <li>每个维度的权重范围为0-100%</li>
              <li>总权重不能超过100%</li>
              <li>可以通过拖动滑块、输入数值或点击 +/- 按钮调整权重</li>
              <li>点击"智能调整"可根据职位描述自动生成权重</li>
              <li>点击"重置默认"可恢复默认权重设置</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
